import React, { useEffect, useRef, useState } from "react";
import {
  useNavigate,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Alert,
  Link,
  Container,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useAuthContext } from "../contexts/AuthContext";
import { useAuth } from "../hooks/useAuth";
import { AuthLayout } from "../components/Layouts";
import { createLoginStyles } from "./styles";
import { useTheme } from "@mui/material/styles";
import { DEFAULT_REDIRECT_PATH, ROUTES } from "../constants/routes";

/**
 * Interface for location state passed via navigation
 */
interface LocationState {
  /** Optional status message passed from previous page */
  status?: string;
}

/**
 * Email Verification Page Component
 *
 * This component handles email verification for users who have registered
 * but haven't verified their email address yet. It supports:
 * - Automatic verification via URL parameters (id, hash, expires)
 * - Manual resending of verification emails
 * - Logout functionality
 * - Redirects verified users to the dashboard
 */
const VerifyEmail: React.FC = () => {
  // Theme and responsive design
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const loginStyles = createLoginStyles(theme);

  // Authentication hooks
  const { logout, sendVerificationEmailLink, verifyUserEmail } = useAuth();

  // Navigation hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Authentication state
  const { isEmailVerified } = useAuthContext();

  // Component state
  /** Current verification status message */
  const [verificationStatus, setVerificationStatus] = useState<string | null>(
    null
  );
  /** Loading state for async operations */
  const [isLoading, setIsLoading] = useState(false);
  /** Error message to display to user */
  const [error, setError] = useState<string | null>(null);
  /** Success message to display to user */
  const [success, setSuccess] = useState<string | null>(null);
  /** Flag to track if initial verification email has been sent */
  const [hasSentInitialEmail, setHasSentInitialEmail] = useState(false);

  // Extract URL parameters for email verification
  const { userId, hash } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const expires = queryParams.get("expires") || undefined;

  /** Boolean flag indicating if all required verification parameters are present */
  const hasVerifyParams = Boolean(userId && hash && expires);

  /** Dev-only guard to avoid StrictMode double-run for initial auto-send */
  const initialSendGuardRef = useRef(false);

  /**
   * Effect to read navigation state once on component mount
   * Extracts any status message passed from the previous page
   */
  useEffect(() => {
    const statusFromState = (location.state as LocationState)?.status || "";
    if (statusFromState) setVerificationStatus(statusFromState);
  }, [location.state]);

  /**
   * Effect to handle automatic email verification when URL contains verification parameters
   * This runs when the user clicks a verification link from their email
   */
  useEffect(() => {
    // Guards to prevent unnecessary verification
    if (isEmailVerified) return;
    if (!hasVerifyParams) return;

    /**
     * Async function to verify email using URL parameters
     * Calls the verification API and handles success/error states
     */
    const verifyOnLoad = async () => {
      setIsLoading(true);
      try {
        await verifyUserEmail(
          userId as string,
          hash as string,
          expires as string
        );
        setSuccess("Your email has been successfully verified!");
        // Redirect to registration survey after successful verification
        navigate(ROUTES.REGISTRATION_SURVEY, { replace: true });
      } catch (err: any) {
        setError(`Email verification failed. ${err.message}.`);
      } finally {
        setIsLoading(false);
      }
    };

    verifyOnLoad();
    // We intentionally depend only on hasVerifyParams to avoid refires
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVerifyParams]);

  /**
   * Effect to automatically send initial verification email when no URL parameters are present
   * This runs when user first lands on the page without verification parameters
   */
  useEffect(() => {
    // Guards to prevent unnecessary email sends
    if (isEmailVerified) return; // User already verified
    if (hasVerifyParams) return; // URL has verification params, don't send email
    if (hasSentInitialEmail) return; // Already sent initial email
    if (initialSendGuardRef.current) return; // Dev guard for StrictMode
    initialSendGuardRef.current = true;

    /**
     * Async function to send the initial verification email
     * Called automatically when user lands on verification page
     */
    const sendInitialVerificationEmail = async () => {
      try {
        setIsLoading(true);
        await sendVerificationEmailLink();
        setSuccess(
          "A new verification link has been sent to your email address."
        );
        setHasSentInitialEmail(true);
      } catch (err: any) {
        setError(`Failed to send verification email. ${err.message}.`);
      } finally {
        setIsLoading(false);
      }
    };

    sendInitialVerificationEmail();
  }, [hasVerifyParams, hasSentInitialEmail]);

  /**
   * Handle resending verification email
   * Called when user clicks the "Resend Verification Email" button
   *
   * @param e - Form submission event
   */
  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    // Clear previous messages
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    // Send verification email
    try {
      await sendVerificationEmailLink();
      setVerificationStatus("verification-link-sent");
    } catch (err: any) {
      setError(`Couldn't send verification email. ${err.message}.`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle user logout
   * Called when user clicks the "Log Out" link
   * Logs out the user and redirects to login page
   */
  const handleLogout = async () => {
    try {
      await logout();
    } catch (err: any) {
      setError(`Failed to log out. ${err.message}.`);
    }
    navigate(ROUTES.LOGIN);
  };

  // Redirect already verified users to registration survey
  if (isEmailVerified) {
    return <Navigate to={ROUTES.REGISTRATION_SURVEY} replace />;
  }

  return (
    <AuthLayout>
      <title>Verify email address - Materiom Commons</title>
      <Container sx={loginStyles.container}>
        <Paper elevation={24} sx={loginStyles.paper}>
          {/* Main title */}
          <Typography variant="h5" component="h2" sx={loginStyles.header}>
            Check your inbox to verify your email
          </Typography>

          {/* Instructions for the user */}
          <Typography
            variant="body2"
            sx={{ mb: 2, color: theme.palette.text.primary, lineHeight: 1.5 }}
          >
            Before getting started, please verify your email by clicking on the
            link we just emailed you. If you haven't received an email from us,
            please click the button below to have another sent.
          </Typography>

          {/* Error message display */}
          {error && (
            <Alert severity="error" sx={loginStyles.errorAlert}>
              {error}
            </Alert>
          )}

          {/* Success message display */}
          {success && (
            <Alert severity="success" sx={loginStyles.errorAlert}>
              {success}
            </Alert>
          )}

          {/* Verification link sent confirmation */}
          {verificationStatus === "verification-link-sent" && (
            <Alert severity="success" sx={loginStyles.errorAlert}>
              A new verification link has been sent to your email address.
            </Alert>
          )}

          {/* Form with resend button and logout link */}
          <Box
            component="form"
            onSubmit={handleResendVerification}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            {/* Resend verification email button */}
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{ ...loginStyles.loginButton, width: "auto" }}
            >
              {isLoading
                ? "Sending..."
                : isSmallScreen
                ? "Resend Email"
                : "Resend Verification Email"}
            </Button>

            {/* Logout link */}
            <Box sx={loginStyles.forgotPasswordContainer}>
              <Link
                component="button"
                type="button"
                onClick={handleLogout}
                sx={loginStyles.forgotPasswordLink}
              >
                Log Out
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </AuthLayout>
  );
};

export default VerifyEmail;
